import { ReactNode } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import type {
  CardAppearance,
  EventLayout,
  EventRenderContext,
  TimeGridEvent,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { GridCard } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/GridCard'

const CELL_BOTTOM_PADDING_PX = 46
interface Props<TEvent extends TimeGridEvent, TDragDrop> {
  layouts: EventLayout<TEvent>[]
  paddingX: number
  paddingY: number
  cardLayout: {
    minHeight: number
    paddingX: number
    paddingY: number
    radius: number
  }
  minColumnWidth: number
  columnCount: number
  eventRenderer: (event: TEvent, context: EventRenderContext<TDragDrop>) => ReactNode
  cardAppearance?: (event: TEvent) => CardAppearance
  dragDropHandlers?: TDragDrop
  selectedCardSlotKeys?: Set<string>
  onCardSlotSelected?: (eventId: string) => void
}

const TimeGridRowEvents = <TEvent extends TimeGridEvent, TDragDrop>({
  layouts,
  paddingX,
  paddingY,
  cardLayout,
  minColumnWidth,
  columnCount,
  eventRenderer,
  cardAppearance,
  dragDropHandlers,
  selectedCardSlotKeys,
  onCardSlotSelected,
}: Props<TEvent, TDragDrop>) => {
  const maxStackCount = layouts.reduce((max, layout) => Math.max(max, layout.stackIndex + 1), 0)
  const hasColumnOverlap = (left: EventLayout<TEvent>, right: EventLayout<TEvent>): boolean => {
    return (
      left.span.startIndex <= right.span.endIndex && left.span.endIndex >= right.span.startIndex
    )
  }

  const getFillHeightRowSpan = (layout: EventLayout<TEvent>): number => {
    // A fill-height card can grow down until the next stacked event that overlaps in columns.
    const closestOverlappingStackAbove = layouts
      .filter((candidate) => {
        if (candidate.event.id === layout.event.id) return false
        if (candidate.stackIndex <= layout.stackIndex) return false

        return hasColumnOverlap(layout, candidate)
      })
      .reduce<number | null>((closest, candidate) => {
        if (closest === null) return candidate.stackIndex

        return Math.min(closest, candidate.stackIndex)
      }, null)

    if (closestOverlappingStackAbove === null) {
      return Math.max(1, maxStackCount - layout.stackIndex)
    }

    return Math.max(1, closestOverlappingStackAbove - layout.stackIndex)
  }

  return (
    <Box
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
        padding: `${paddingY}px 0 ${CELL_BOTTOM_PADDING_PX}px 0`,
        boxSizing: 'border-box',
      }}
    >
      {/* Events are placed in a CSS grid so row height grows with stacked items. */}
      {layouts.map((layout) => {
        const spanWidth = layout.span.endIndex - layout.span.startIndex + 1
        const gridColumn = `${layout.span.startIndex + 1} / span ${spanWidth}`
        const appearance = cardAppearance?.(layout.event)
        const fillHeightRowSpan = getFillHeightRowSpan(layout)
        const gridRow = appearance?.fillHeight
          ? `${layout.stackIndex + 1} / span ${fillHeightRowSpan}`
          : `${layout.stackIndex + 1}`

        return (
          <GridCard
            key={layout.event.id}
            gridColumn={gridColumn}
            gridRow={gridRow}
            minHeight={cardLayout.minHeight}
            paddingX={paddingX}
            cardPaddingX={cardLayout.paddingX}
            cardPaddingY={cardLayout.paddingY}
            radius={cardLayout.radius}
            appearance={appearance}
            isSelected={selectedCardSlotKeys?.has(layout.event.id) ?? false}
            onCardSlotSelected={
              onCardSlotSelected ? () => onCardSlotSelected(layout.event.id) : undefined
            }
          >
            {eventRenderer(layout.event, {
              span: layout.span,
              stackIndex: layout.stackIndex,
              clampedRange: layout.clampedRange,
              dragDropHandlers,
            })}
          </GridCard>
        )
      })}
    </Box>
  )
}

export { TimeGridRowEvents }
